import React, { useRef } from "react";
import { faImages } from "@fortawesome/free-solid-svg-icons";
import { FileUploader } from "react-drag-drop-files";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ImageResolutionTooltip from "../ConfigurationNewHotel/ImageResolutionTooltip";
import {
  getAddressTextCharacterCount,
  resolveAddressFieldChange,
  normalizeAddressText,
  prepareAddressTextForSubmit,
  WELCOME_LETTER_ADDRESS_MAX_LENGTH,
  WELCOME_LETTER_IMAGE_SPECS,
} from "./welcomeLetterHtmlUtils";

function WelcomeLetterOptionalFields({
  register,
  errors,
  watch,
  setValue,
  hotelLogoPreviewUrl,
  hotelLogoFileError,
  onHotelLogoUpload,
  onHotelLogoRemove,
  signaturePreviewUrl,
  signatureFileError,
  onSignatureUpload,
  onSignatureRemove,
}) {
  const {
    onBlur,
    name,
    ref,
  } = register("topRightText", {
    validate: (value) => prepareAddressTextForSubmit(value || "").error || true,
  });

  const addressValue = watch("topRightText") || "";
  const addressPastePendingRef = useRef(false);

  const isPasteInput = (nativeEvent) => {
    const inputType = nativeEvent?.inputType;
    return inputType === "insertFromPaste" || inputType === "insertFromPasteAsQuotation";
  };

  const handleAddressPaste = () => {
    addressPastePendingRef.current = true;
  };

  const handleAddressChange = (event) => {
    const isPaste = addressPastePendingRef.current || isPasteInput(event.nativeEvent);
    addressPastePendingRef.current = false;

    const { shouldUpdate, value } = resolveAddressFieldChange(event.target.value, { isPaste });
    if (!shouldUpdate) {
      return;
    }

    setValue("topRightText", value, { shouldValidate: true, shouldDirty: true });
  };

  const handleAddressBlur = (event) => {
    const normalized = normalizeAddressText(event.target.value);
    if (normalized !== event.target.value) {
      setValue("topRightText", normalized, { shouldValidate: true, shouldDirty: true });
    }
    onBlur(event);
  };

  return (
    <div className="row welcome-letter-optional-fields">
      <div className="form-group-hotel col-4 welcome-letter-field-column">
        <p className="mb-1 Roboto-Bold">Hotel Logo</p>
        {hotelLogoPreviewUrl
          ? (
            <div className="welcome-letter-field-box welcome-letter-logo-preview">
              <img src={hotelLogoPreviewUrl} alt="hotel-logo-preview" />
              <span className="welcome-letter-remove-btn" onClick={onHotelLogoRemove}>X</span>
            </div>
          )
          : (
            <FileUploader
              name="hotelLogo"
              types={["JPG", "PNG", "JPEG"]}
              handleChange={onHotelLogoUpload}
              classes="drag-file file-upload-bg welcome-letter-upload"
              children={
                <>
                  <FontAwesomeIcon icon={faImages} className="fa-lg" />
                  <p className="drag-text">
                    Drag and drop or <span>Browse</span>
                    <br />
                    <span className="Roboto-Bold text-black">Hotel Logo</span>
                    <ImageResolutionTooltip data={WELCOME_LETTER_IMAGE_SPECS.hotelLogo} />
                  </p>
                </>
              }
            />
          )}
        {hotelLogoFileError && <small className="required">{hotelLogoFileError}</small>}
      </div>
      <div className="form-group-hotel col-4 welcome-letter-field-column">
        <p className="mb-1 Roboto-Bold">
          Address
          <span className="welcome-letter-address-limit fnt-12px ms-1">
            (Maximum: {WELCOME_LETTER_ADDRESS_MAX_LENGTH} characters)
          </span>
        </p>
        <textarea
          className="form-control welcome-letter-address"
          placeholder="Enter address ..."
          name={name}
          ref={ref}
          value={addressValue}
          onChange={handleAddressChange}
          onPaste={handleAddressPaste}
          onBlur={handleAddressBlur}
        />
        <p className="text-end fnt-12px mb-0 welcome-letter-address-count">
          {getAddressTextCharacterCount(addressValue)} / {WELCOME_LETTER_ADDRESS_MAX_LENGTH}
        </p>
        {errors?.topRightText && (
          <small className="required">{errors.topRightText.message}</small>
        )}
      </div>
      <div className="form-group-hotel col-4 welcome-letter-field-column">
        <p className="mb-1 Roboto-Bold">Signature</p>
        {signaturePreviewUrl
          ? (
            <div className="welcome-letter-field-box welcome-letter-signature-preview">
              <img src={signaturePreviewUrl} alt="signature-preview" />
              <span className="welcome-letter-remove-btn" onClick={onSignatureRemove}>X</span>
            </div>
          )
          : (
            <FileUploader
              name="signatureImage"
              types={["JPG", "PNG", "JPEG"]}
              handleChange={onSignatureUpload}
              classes="drag-file file-upload-bg welcome-letter-upload"
              children={
                <>
                  <FontAwesomeIcon icon={faImages} className="fa-lg" />
                  <p className="drag-text">
                    Drag and drop or <span>Browse</span>
                    <br />
                    <span className="Roboto-Bold text-black">Signature</span>
                    <ImageResolutionTooltip data={WELCOME_LETTER_IMAGE_SPECS.signature} />
                  </p>
                </>
              }
            />
          )}
        {signatureFileError && <small className="required">{signatureFileError}</small>}
      </div>
    </div>
  );
}

export default WelcomeLetterOptionalFields;
